<script>
    import { format_age_range_compact } from './utils/format_ages';
    import { get_buy_path } from './utils/get_product_path';

    /** @type {Product} */
    export let product;

    /** @type {ReviewDb[] | null} */
    export let reviews = null;

    $: schema = generateSchema(product, reviews);

    /**
     * @param {Product} product
     * @param {ReviewDb[] | null} reviews
     * */
    function generateSchema(product, reviews) {
        // Skip schema for products without price
        if (!product.price) {
            return null;
        }

        const base_url = 'https://critikid.com';

        /**@type {Record<string, any>}*/
        let schema;

        switch (product.type) {
            case 'course':
                schema = {
                    '@context': 'https://schema.org',
                    '@type': ['Product', 'Course'],
                    name: product.title,
                    description: product.head_description,
                    provider: {
                        '@type': 'Organization',
                        name: 'Critikid',
                        url: base_url
                    },
                    courseMode: 'online',
                    url: `${base_url}/courses/${product.slug}`,
                    image: product.og_image
                        ? `${base_url}/${product.og_image}`
                        : undefined,
                    offers: {
                        '@type': 'Offer',
                        category: 'Paid',
                        url: `${base_url}/courses/${product.slug}`,
                        priceCurrency: product.price.price_fx,
                        price: product.price.price?.toString() || 0,
                        availability: 'https://schema.org/OnlineOnly'
                    },
                    hasCourseInstance: {
                        '@type': 'CourseInstance',
                        courseMode: 'Online',
                        courseWorkload: `PT${product.workload}H`
                    }
                };

                // Add video if trailer exists
                if (product.trailer_playback_id) {
                    schema.video = {
                        '@type': 'VideoObject',
                        name: `${product.title} – Trailer`,
                        description: `Preview of the ${product.title} course.`,
                        thumbnailUrl: `https://image.mux.com/${product.trailer_playback_id}/thumbnail.jpg?width=1280&height=720&fit_mode=pad&time=${product.trailer_thumbnail_time}`,
                        embedUrl: `${base_url}/courses/${product.slug}`,
                        contentUrl: `https://stream.mux.com/${product.trailer_playback_id}/highest.mp4`,
                        uploadDate: product.date
                    };
                }
                break;

            case 'worksheet':
                schema = {
                    '@context': 'https://schema.org',
                    '@type': 'Product',
                    name: product.title,
                    description: (
                        product.lead || product.head_description
                    )?.replace(/<[^>]*>/g, ''),
                    brand: {
                        '@type': 'Organization',
                        name: 'Critikid',
                        url: base_url
                    },
                    url: `${base_url}/worksheets/${product.slug}`,
                    image:
                        product.preview_images &&
                        product.preview_images.length > 0
                            ? `${base_url}/${product.preview_images[0]}`
                            : undefined,
                    offers: {
                        '@type': 'Offer',
                        url: `${base_url}/worksheets/${product.slug}`,
                        priceCurrency: product.price.price_fx,
                        price: product.price.price?.toString() || 0,
                        availability: 'https://schema.org/OnlineOnly'
                    }
                };
                break;

            case 'bundle':
                schema = {
                    '@context': 'https://schema.org',
                    '@type': 'Product',
                    name: product.title,
                    description: (
                        product.lead || product.head_description
                    )?.replace(/<[^>]*>/g, ''),
                    brand: {
                        '@type': 'Organization',
                        name: 'Critikid',
                        url: base_url
                    },
                    url: `${base_url}/bundles/${product.slug}`,
                    image: product.og_image
                        ? `${base_url}/${product.og_image}`
                        : undefined,
                    offers: {
                        '@type': 'Offer',
                        url: `${base_url}/bundles/${product.slug}`,
                        priceCurrency: product.price.price_fx,
                        price: product.price.price.toString(),
                        availability: 'https://schema.org/OnlineOnly'
                    },
                    hasPart: product.items?.map((item) => {
                        const part = {
                            name: item.title,
                            description: item.lead
                                ?.replace(/<[^>]*>/g, '')
                                .trim(),
                            image: `${base_url}/${item.icon}`,

                            // The item's own standalone price, not the bundle's.
                            offers: item.price
                                ? {
                                      '@type': 'Offer',
                                      priceCurrency: item.price.price_fx,
                                      price:
                                          item.price.price?.toString() || 0,
                                      availability:
                                          'https://schema.org/OnlineOnly'
                                  }
                                : undefined
                        };

                        if (item.type === 'course') {
                            part['@type'] = ['Product', 'Course'];
                            part['courseMode'] = 'online';
                            part['provider'] = {
                                '@type': 'Organization',
                                name: 'Critikid',
                                url: base_url
                            };
                            part['url'] = `${base_url}/courses/${item.slug}`;
                        } else {
                            // worksheets and extras are products
                            part['@type'] = 'Product';
                            if (item.type === 'worksheet') {
                                part.url = `${base_url}/worksheets/${item.slug}`;
                            }
                        }

                        return part;
                    })
                };
                break;

            default:
                return null;
        }

        // Same age fields as ProductListSchema, so list and detail schemas agree.
        const age_range = format_age_range_compact(
            product.min_age,
            product.max_age
        );
        if (age_range && product.type === 'course') {
            schema.typicalAgeRange = age_range;
        } else if (age_range) {
            schema.audience = {
                '@type': 'PeopleAudience',
                suggestedMinAge: product.min_age || 5,
                ...(product.max_age && { suggestedMaxAge: product.max_age })
            };
        }

        // Direct checkout link (see BuyPage.svelte) for agents reading the schema.
        const buy_path = get_buy_path(product);
        if (buy_path) {
            schema.potentialAction = {
                '@type': 'BuyAction',
                target: `${base_url}${buy_path}`
            };
        }

        // Add reviews if they exist
        if (reviews && reviews.length > 0) {
            const normalized = reviews
                .map((review) => ({
                    rating: review.review_rating ?? null,
                    author: review.username || 'Anonymous',
                    text: review.review_text || ''
                }))
                .filter((r) => r.rating && r.text);

            if (normalized.length > 0) {
                const total_rating = normalized.reduce(
                    (sum, review) => sum + (review.rating || 0),
                    0
                );
                const avg_rating =
                    Math.round((total_rating / normalized.length) * 10) / 10;

                schema.aggregateRating = {
                    '@type': 'AggregateRating',
                    ratingValue: avg_rating,
                    reviewCount: normalized.length,
                    bestRating: 5,
                    worstRating: 1
                };

                schema.review = normalized.map((review) => ({
                    '@type': 'Review',
                    author: {
                        '@type': 'Person',
                        name: review.author
                    },
                    reviewRating: {
                        '@type': 'Rating',
                        ratingValue: review.rating,
                        bestRating: 5,
                        worstRating: 1
                    },
                    reviewBody: review.text
                }));
            }
        }

        return schema;
    }
</script>

{#if schema}
    {@html `<script type="application/ld+json">${JSON.stringify(
        schema,
        null,
        2
    )}</script>`}
{/if}
